<template>
  <div
    class="messenger-container"
    :class="[
      `messenger-container--align-${sharedSettings.placement.alignTo}`,
      { 'messenger-container--minimized': isMinimized },
    ]"
  >
    <messenger-widget
      v-if="currentViewName !== 'idle'"
      class="messenger-container__widget"
      :view-name="currentViewName"
      :shared-settings="sharedSettings"
      :minimized-view-settings="minimizedViewSettings"
      :is-tooltip-visible="eyeCatcher.isVisible.value"
      :is-popover-visible="!!openedPopover"
      :has-notification="chatStore.hasNotViewedNewMessages.value"
      :width100pr="isParentSmaller"
      @expand="expandWidgetHandler"
      @minimize="onWidgetMinimize"
      @resize="onResize"
      @close-tooltip="eyeCatcher.close()"
    >
      <template v-slot:view-welcome>
        <messenger-welcome-container />
      </template>

      <template v-slot:view-email>
        <messenger-email-container
          :form-values-cache="formCache.formValues.value.email"
          @submit="goToViewWithinChannel('emailSuccess')"
          @back="onBackFromChannel(WebWidgetChannelType.EMAIL)"
          @update-form-values-cache="formCache.updateFormValues('email', $event)"
        />
      </template>

      <template v-slot:view-emailSuccess>
        <messenger-widget-success-view
          :view-settings="emailSuccessViewSettings"
          @start-over="goToMainView"
          @close="closeWidget"
        />
      </template>

      <template v-slot:view-sms>
        <messenger-widget-sms-view
          :view-settings="smsViewSettings"
          @back="onBackFromChannel(WebWidgetChannelType.SMS)"
        />
      </template>

      <template v-slot:view-call>
        <messenger-widget-call-view
          :view-settings="callViewSettings"
          @back="onBackFromChannel(WebWidgetChannelType.CALL)"
        />
      </template>

      <template v-slot:view-chat="{ setExpandedViewCustomHeight }">
        <messenger-chat-container
          :form-values-cache="formCache.formValues.value.chat"
          @back="onBackFromChannel(WebWidgetChannelType.CHAT)"
          @resize="setExpandedViewCustomHeight($event)"
          @start-new-chat="onChatLeave()"
          @close-window="closeWidget()"
          @update-form-values-cache="formCache.updateFormValues('chat', $event)"
        />
      </template>

      <template v-slot:view-whatsapp>
        <messenger-widget-whatsapp-view
          :view-settings="whatsappViewSettings"
          @back="onBackFromChannel(WebWidgetChannelType.WHATSAPP)"
        />
      </template>

      <template v-slot:view-facebook>
        <messenger-widget-facebook-view
          :view-settings="facebookViewSettings"
          @back="onBackFromChannel(WebWidgetChannelType.FACEBOOK)"
        />
      </template>

      <template v-slot:view-instagram>
        <messenger-widget-instagram-view
          :view-settings="instagramViewSettings"
          @back="onBackFromChannel(WebWidgetChannelType.INSTAGRAM)"
        />
      </template>

      <template v-slot:popover>
        <messenger-popovers-container />
      </template>
    </messenger-widget>
  </div>
</template>

<script setup lang="ts">
import MessengerWidget from '@widgets/messenger/components/MessengerWidget.vue'
import type { MessengerResizePayload } from '@widgets/messenger/types'
import MessengerEmailContainer from '@widgets/messenger/containers/views/MessengerEmailContainer.vue'
import MessengerWidgetSuccessView from '@widgets/messenger/components/views/MessengerWidgetSuccessView.vue'
import { useQuasarIcons } from '@widgets/shared/compositions/quasarIcons'
import { sendMessengerPositionMessage } from '@widgets/messenger/utils/positionMessages'
import { makeWidgetSharedSettings } from '@widgets/messenger/compositions/useWidgetSharedSettings'
import { useWidgetMinimizedViewSettings } from '@widgets/messenger/compositions/useWidgetMinimizedViewSettings'
import { useWidgetWelcomeViewSettings } from '@widgets/messenger/compositions/useWidgetWelcomeViewSettings'
import { useWidgetEmailSuccessViewSettings } from '@widgets/messenger/compositions/useWidgetEmailSuccessViewSettings'
import { useWidgetEyeCatcher } from '@widgets/messenger/compositions/useWidgetEyeCatcher'
import MessengerWidgetCallView from '@widgets/messenger/components/views/MessengerWidgetCallView.vue'
import { useWidgetCallViewSettings } from '@widgets/messenger/compositions/useWidgetCallViewSettings'
import { useWidgetFormCache } from '@widgets/messenger/compositions/useWidgetFormCache'
import { useWidgetNavigation } from '@widgets/messenger/compositions/useWidgetNavigation'
import MessengerChatContainer from '@widgets/messenger/containers/views/MessengerChatContainer.vue'
import { useWidgetShadowOffset } from '@widgets/messenger/compositions/useWidgetShadowOffset'
import MessengerPopoversContainer from '@widgets/messenger/containers/MessengerPopoversContainer.vue'
import { useWidgetPopovers } from '@widgets/messenger/compositions/useWidgetPopovers'
import { useWidgetChat } from '@widgets/messenger/compositions/useWidgetChat'
import { useWidgetChatBroadcastEvents } from '@widgets/messenger/compositions/useWidgetChatBroadcastEvents'
import { useWidgetChatInit } from '@widgets/messenger/compositions/useWidgetChatInit'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { useChatStore } from '@widgets/messenger/stores/useChatStore'
import MessengerWidgetWhatsappView from '@widgets/messenger/components/views/MessengerWidgetWhatsappView.vue'
import { useWidgetWhatsappViewSettings } from '@widgets/messenger/compositions/useWidgetWhatsappViewSettings'
import MessengerWelcomeContainer from '@widgets/messenger/containers/views/MessengerWelcomeContainer.vue'
import { useWidgetChatConnectionAlerts } from '@widgets/messenger/compositions/useWidgetChatConnectionAlerts'
import MessengerWidgetSmsView from '@widgets/messenger/components/views/MessengerWidgetSmsView.vue'
import { useWidgetSmsViewSettings } from '@widgets/messenger/compositions/useWidgetSmsViewSettings'
import MessengerWidgetFacebookView from '@widgets/messenger/components/views/MessengerWidgetFacebookView.vue'
import MessengerWidgetInstagramView from '@widgets/messenger/components/views/MessengerWidgetInstagramView.vue'
import { useWidgetFacebookViewSettings } from '@widgets/messenger/compositions/useWidgetFacebookViewSettings'
import { useWidgetInstagramViewSettings } from '@widgets/messenger/compositions/useWidgetInstagramViewSettings'
import { useWidgetParentSize } from '@widgets/messenger/compositions/useWidgetParentSize'
import { computed, onBeforeUnmount, onMounted, watch } from '@/composition/vue/compositionApi'
import { WebWidgetChannelType } from '@/data/models/domain/messages/settings/webWidget/types'

useQuasarIcons()

const widgetStore = useWidgetStore()
const chatStore = useChatStore()
const sharedSettings = computed(() => makeWidgetSharedSettings(widgetStore.widgetSettings.value))
const minimizedViewSettings = useWidgetMinimizedViewSettings()
const welcomeViewSettings = useWidgetWelcomeViewSettings()
const emailSuccessViewSettings = useWidgetEmailSuccessViewSettings()
const smsViewSettings = useWidgetSmsViewSettings()
const callViewSettings = useWidgetCallViewSettings()
const whatsappViewSettings = useWidgetWhatsappViewSettings()
const facebookViewSettings = useWidgetFacebookViewSettings()
const instagramViewSettings = useWidgetInstagramViewSettings()

const eyeCatcher = useWidgetEyeCatcher()
const formCache = useWidgetFormCache()
const { openedPopover, subscribeOnPopoverClosed, unsubscribeFromPopoverClosed } = useWidgetPopovers()
const { hasChatChannel } = useWidgetChat()
const { checkAndConnectToExistingChat } = useWidgetChatInit()
const chatBroadcastEvents = useWidgetChatBroadcastEvents()

const {
  currentViewName,
  init,
  minimize,
  goToMainView,
  onWidgetExpand,
  onWidgetMinimize,
  onBackFromChannel,
  goToViewWithinChannel,
} = useWidgetNavigation()

const { initConnectionAlerts } = useWidgetChatConnectionAlerts()

const closeWidget = () => {
  formCache.resetAll()
  minimize(undefined)
}

const expandWidgetHandler = () => {
  onWidgetExpand()
  eyeCatcher.close()
}

const onChatLeave = () => {
  chatBroadcastEvents.emitChatLeave()
  chatStore.resetStore()
}

// It is necessary to prevent the widget flickering when changing the view
// This is because the iframe's size changes little bit later than the widget's size
watch(
  () => sharedSettings.value.placement.alignTo,
  (alignTo) => {
    const appElement = document.getElementById('app')!
    appElement.classList.remove('align-right', 'align-left')
    if (alignTo === 'left') {
      appElement.classList.add('align-left')
    } else {
      appElement.classList.add('align-right')
    }
  },
  { immediate: true },
)

const messengerWidgetShadowOffset = useWidgetShadowOffset(widgetStore.uniqueCode.value)

const onResize = (payload: MessengerResizePayload) => {
  sendMessengerPositionMessage(widgetStore.uniqueCode.value, {
    alignTo: sharedSettings.value.placement.alignTo,
    sideSpacing: sharedSettings.value.placement.sideSpacing,
    bottomSpacing: sharedSettings.value.placement.bottomSpacing,
    width: payload.width + 2 * messengerWidgetShadowOffset.value,
    height: payload.height + 2 * messengerWidgetShadowOffset.value,
    isMinimized: currentViewName.value === 'minimized',
    startButtonSize: payload.startButtonSize,
  })
}

const isMinimized = computed(() => currentViewName.value === 'minimized')

const { isParentSmaller } = useWidgetParentSize(widgetStore.uniqueCode.value)

const setupChat = async () => {
  chatBroadcastEvents.onChatStart(async () => {
    await checkAndConnectToExistingChat()
  })
  chatBroadcastEvents.onChatLeave(() => {
    chatStore.resetStore()
  })

  return await checkAndConnectToExistingChat()
}

let popoverClosedSubscriptionId = ''

onMounted(async () => {
  initConnectionAlerts()

  if (hasChatChannel.value) {
    await setupChat()
  }
  init(chatStore.isActiveChat.value)

  if (!welcomeViewSettings.value.channels.length) {
    expandWidgetHandler()
  } else if (isMinimized.value) {
    eyeCatcher.run()
  }

  popoverClosedSubscriptionId = subscribeOnPopoverClosed()
})

onBeforeUnmount(() => {
  unsubscribeFromPopoverClosed(popoverClosedSubscriptionId)
})
</script>

<style lang="scss" scoped>
.messenger-container {
  display: flex;
  align-items: flex-end;
  height: 100%;
  // Shadow offset compensation to prevent shadow truncation
  padding: calc(v-bind(messengerWidgetShadowOffset) * 1px);

  &:not(&--minimized) {
    width: 100%;
  }

  &__widget {
    max-height: 100%;
    max-width: 100%;
  }

  &--align-left {
    justify-content: flex-start;
  }

  &--align-right {
    justify-content: flex-end;
  }
}
</style>

<!-- eslint-disable-next-line vue/enforce-style-attribute -->
<style lang="scss">
#app {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: flex-end;

  &.align-left {
    justify-content: flex-start;
  }

  &.align-right {
    justify-content: flex-end;
  }
}
</style>
